import type { ReactNode } from "react";
import { NavLink, matchPath, useLocation, useNavigate } from "react-router-dom";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";

export interface TabOption {
  value: string;
  label: ReactNode;
}

/** Strips with at least this many tabs collapse into a `TabSelect` below md
 * (owner 2026-09-12): a 7–9 item strip on a phone is a blind horizontal
 * scroll; one Select shows the current tab and lists the rest. */
export const MOBILE_SELECT_MIN = 4;

/** Phone stand-in for a long tab strip — the shadcn Tabs→Select pattern.
 * Same options, same value/onValueChange as the strip it replaces. */
export const TabSelect = ({
  options,
  value,
  onValueChange,
  className,
}: {
  options: TabOption[];
  value: string;
  onValueChange: (value: string) => void;
  className?: string;
}) => (
  <Select value={value} onValueChange={onValueChange}>
    <SelectTrigger size="sm" className={cn("w-fit", className)}>
      <SelectValue />
    </SelectTrigger>
    <SelectContent>
      {options.map((o) => (
        <SelectItem key={o.value} value={o.value}>
          {o.label}
        </SelectItem>
      ))}
    </SelectContent>
  </Select>
);

// Underline tab bar on top of shadcn Tabs `variant="line"` — the one tab
// look for route-driven (RouteTabs) and state-driven (Tabs + TabBar) bars.
// Replaces the retired zthd/tabs (hand-rolled NavLink/button strip).

/** Row shell: full-width bottom border, tab strip left, optional action
 * right. Narrow screens scroll the strip horizontally instead of wrapping;
 * below md the action group moves onto its own line above the strip so the
 * strip keeps the full width. Pass `options` + `value` + `onValueChange`
 * and a strip of MOBILE_SELECT_MIN+ tabs renders as a `TabSelect` below md
 * (action beside it on the same line). Must render inside a shadcn `<Tabs>`
 * root. */
export const TabBar = ({
  children,
  action,
  className,
  options,
  value,
  onValueChange,
}: {
  children: ReactNode;
  action?: ReactNode;
  className?: string;
  options?: TabOption[];
  value?: string;
  onValueChange?: (value: string) => void;
}) => {
  const mobileSelect =
    !!options &&
    options.length >= MOBILE_SELECT_MIN &&
    value !== undefined &&
    !!onValueChange;
  return (
    <div
      className={cn(
        "flex w-full flex-wrap items-center justify-between gap-2 border-b",
        className
      )}
    >
      {mobileSelect && (
        <div className="pb-1 md:hidden">
          <TabSelect
            options={options}
            value={value}
            onValueChange={onValueChange}
          />
        </div>
      )}
      {/* The list fills the row (so it can scroll and push the action
          right), but each trigger keeps its natural width — shadcn's
          TabsTrigger is flex-1 and would otherwise split the row evenly. */}
      <TabsList
        variant="line"
        className={cn(
          "min-w-0 flex-1 justify-start overflow-x-auto *:flex-none",
          mobileSelect && "hidden md:flex"
        )}
      >
        {children}
      </TabsList>
      {/* Rendered once (a Dialog trigger here must not mount twice): with the
          phone Select it shares that line; without it, it takes its own line
          above the strip below md. */}
      {action && (
        <div
          className={cn(
            "flex shrink-0 justify-end pb-1 md:w-auto",
            !mobileSelect && "max-md:order-first max-md:w-full"
          )}
        >
          {action}
        </div>
      )}
    </div>
  );
};

export interface RouteTabItem {
  to: string;
  name: ReactNode;
  /** Exact match only (index tab), same as NavLink `end`. */
  end?: boolean;
}

/** Route-driven tab bar: each tab is a real `<NavLink>` (middle-click,
 * hover URL), the active one is the route that matches the location. */
export const RouteTabs = ({
  items,
  action,
  className,
}: {
  items: RouteTabItem[];
  action?: ReactNode;
  className?: string;
}) => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const active =
    items.find((it) => matchPath({ path: it.to, end: !!it.end }, pathname))
      ?.to ?? "";
  return (
    <Tabs value={active} className={cn("mb-3", className)}>
      <TabBar
        action={action}
        options={items.map((it) => ({ value: it.to, label: it.name }))}
        value={active}
        onValueChange={(to) => navigate(to)}
      >
        {items.map((it) => (
          <TabsTrigger key={it.to} value={it.to} asChild>
            <NavLink to={it.to} end={it.end}>
              {it.name}
            </NavLink>
          </TabsTrigger>
        ))}
      </TabBar>
    </Tabs>
  );
};

export default RouteTabs;
